import React, {useEffect, useMemo, useState} from "react";
import {Box, Checkbox, CHECKBOX_PARTIAL, LoadingDecorator} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import styles from "./AlertsGrid.module.scss";
import AlertsGridRow from "@/pages/FleetMonitoring/AlertsGrid/AlertsGridRow";
import {EnabledAlertDetails} from "@/models/server/EnabledAlertDetails";
import {getEnabledAlertDetailsAsync, getEnabledAlertsDetailsAsync} from "@/services/FleetService";
import {SubscriptionAlertConfig} from "@/components/AlertSubscriptionsManagement/AlertSubscriptionsManagement";
import {EnabledAlertModel} from "@/models/server/EnabledAlertModel";
import {deleteAlarmAsync, subscribeToAlert} from "@/services/AlarmService";
import {AlarmTypeHelper} from "@/helpers/AlarmTypeHelper";
import BaseAlarmModel from "@/models/BaseAlarmModel";

interface IAlertsGridProps {
    isLoading: boolean;
    data: EnabledAlertGridModel[];
    organizationId: string | null;
    contractId: string | null;
    constructionSiteId: string | null;
    onRowCheckboxClick: (selectedDevices: string[]) => void;
    selectedItemKeys: Set<string>;
    onDeviceSubscriptionsChange: (rentaId: string, alertsConfig: SubscriptionAlertConfig[], newAlerts: BaseAlarmModel[], removedAlertIds: string[]) => void;
    gridRef: React.RefObject<HTMLDivElement>;
}

export type EnabledAlertGridModel = EnabledAlertModel & {
    alertsConfig: SubscriptionAlertConfig[];
}

const AlertsGrid: React.FC<IAlertsGridProps> = ({
    isLoading,
    data,
    organizationId,
    contractId,
    constructionSiteId,
    onRowCheckboxClick,
    selectedItemKeys,
    onDeviceSubscriptionsChange,
    gridRef,
}) => {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [deviceDetailsMap, setDeviceDetailsMap] = useState<{ [key: string]: EnabledAlertDetails | null }>({});

    useEffect(() => {
        // Reset expanded items when data changes
        if (isLoading) {
            setExpandedItems(new Set());
            setDeviceDetailsMap({});
        }
    }, [isLoading]);

    const headerCheckboxChecked = useMemo(() => {
        const selectedCount = selectedItemKeys.size;

        return selectedCount === 0 ? false : selectedCount === data.length ? true : CHECKBOX_PARTIAL;
    }, [data, selectedItemKeys]);

    const handleAccordionButtonClick = async (rentaId: string) => {
        setExpandedItems(prevExpandedItems => {
            const newExpandedItems = new Set(prevExpandedItems);
            if (newExpandedItems.has(rentaId)) {
                newExpandedItems.delete(rentaId);
            } else {
                newExpandedItems.add(rentaId);
            }
            return newExpandedItems;
        });

        if (!deviceDetailsMap[rentaId]) {
            try {
                const details = await getEnabledAlertDetailsAsync(rentaId);
                setDeviceDetailsMap(prevDetails => ({...prevDetails, [rentaId]: details}));
            } catch (error) {
                console.error('Error loading device details:', error);
                setDeviceDetailsMap(prevDetails => ({...prevDetails, [rentaId]: null}));
            }
        }
    };

    const handleHeaderAccordionClick = async () => {
        const allExpanded = expandedItems.size === data.length;

        if (allExpanded) {
            // Collapse all items
            setExpandedItems(new Set());

            return;
        }

        // Expand all items
        const newExpandedItems = new Set(data.map(item => item.rentaId));
        setExpandedItems(newExpandedItems);

        // Fetch device details for all items that are not already in the map
        const fetchRequest = data
            .filter((item) => !deviceDetailsMap[item.rentaId])
            .map(({rentaId}) => rentaId);

        if (!fetchRequest.length) {
            return;
        }

        try {
            const details = await getEnabledAlertsDetailsAsync(fetchRequest);
            const detailsMap = details.reduce((map, detail) => {
                map[detail.rentaId] = detail;
                return map;
            }, {} as { [key: string]: EnabledAlertDetails });
            setDeviceDetailsMap(prevDetails => ({...prevDetails, ...detailsMap}));
        } catch (error) {
            console.error('Error loading devices details:', error);
            const detailsMap = fetchRequest.reduce((map, detail) => {
                map[detail] = null;
                return map;
            }, {} as { [key: string]: EnabledAlertDetails | null });
            setDeviceDetailsMap(prevDetails => ({...prevDetails, ...detailsMap}));
        }
    };

    const saveDeviceSubscriptions = async (device: EnabledAlertGridModel, subscriptions: SubscriptionAlertConfig[]) => {
        const subscriptionsToBeAdded = subscriptions
            .filter((newAlert, index) => newAlert.supported && newAlert.enabled && !device.alertsConfig[index].enabled);
        const subscriptionTypesToBeRemoved = subscriptions
            .filter((newAlert, index) => newAlert.supported && device.alertsConfig![index].enabled && !newAlert.enabled)
            .map(alert => alert.type);
        const subscriptionsToBeUpdated = subscriptions
            .filter((newAlert, index) => newAlert.supported && newAlert.enabled && device.alertsConfig[index].enabled
                && JSON.stringify(newAlert.alarmData) !== JSON.stringify(device.alertsConfig[index].alarmData));

        // Extend subscriptionsToBeAdded with updated subscriptions
        const allSubscriptionsToBeAdded = [
            ...subscriptionsToBeAdded,
            ...subscriptionsToBeUpdated
        ];

        // Extend subscriptionTypesToBeRemoved with updated subscriptions
        const allSubscriptionTypesToBeRemoved = [
            ...subscriptionTypesToBeRemoved,
            ...subscriptionsToBeUpdated.map(alert => alert.type)
        ];

        const newAlerts: BaseAlarmModel[] = [];
        for (const {type, alarmData} of allSubscriptionsToBeAdded) {
            const newAlert = await subscribeToAlert(type, device, constructionSiteId, contractId, organizationId, alarmData);
            if (newAlert) {
                newAlerts.push({id: newAlert.id, alarmType: newAlert.alarmType, alarmData: alarmData ?? null});
            }
        }

        const removedAlertIds: string[] = [];
        for (const alertType of allSubscriptionTypesToBeRemoved) {
            const id = AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, alertType)?.id;
            if (id) {
                await deleteAlarmAsync(id);
                removedAlertIds.push(id);
            }
        }

        onDeviceSubscriptionsChange(device.rentaId, subscriptions, newAlerts, removedAlertIds);
    };

    const handleHeaderCheckboxClick = () => {
        // If all checkboxes are selected, deselect them. Otherwise, select all checkboxes.
        const selectedDevices = selectedItemKeys.size === data.length ? [] : data.map(item => item.rentaId);

        // Update parent component what items are selected.
        onRowCheckboxClick(selectedDevices);
    };

    const handleRowCheckboxClick = (itemKey: string) => {
        const selectedDevices = selectedItemKeys.has(itemKey)
            ? [...selectedItemKeys].filter(key => key !== itemKey)
            : [...selectedItemKeys, itemKey];

        onRowCheckboxClick(selectedDevices);
    };

    return (
        <div className={styles.dataTable} ref={gridRef} data-cy="enabled-alerts-grid">
            {/* Header Row */}
            <div className={`${styles.gridRow} ${styles.dataRow} ${styles.header}`}>
                <div className={styles.name}>
                    <div className={styles.checkboxCell}>
                        <Checkbox checked={headerCheckboxChecked} onChange={handleHeaderCheckboxClick}/>
                    </div>
                    <div className={styles.fontMedium}>{Localizer.fleetMonitoringPageGridDeviceName}</div>
                </div>
                <div className={styles.rentaId}>{Localizer.fleetMonitoringPageGridId}</div>
                <div className={styles.subscriptions}>{Localizer.fleetMonitoringPageAlertSourcesSubscriptions}</div>
                <div className={`${styles.gridItem} ${styles.actions}`}>
                    <div className={styles.accordionButton} onClick={handleHeaderAccordionClick}>
                        <i className={`fas fa-angle-down ${(data.length > 0 && expandedItems.size === data.length) ? styles.rotated : ''}`}></i>
                    </div>
                </div>
            </div>

            {/* Loading Rows */}
            {isLoading && [...Array(4)].map((_, index) => (
                <LoadingDecorator key={index} className={styles.loaderContainer} hideConentOnLoading={false} isLoading={isLoading}/>
            ))}

            {/* Display information about no data */}
            {!isLoading && data.length === 0 &&
                <Box display="flex" alignItems="center" justifyContent="center" className={styles.loaderContainer}>
                    {Localizer.fleetMonitoringPageGridEmpty}
                </Box>
            }

            {/* Data Rows */}
            {!isLoading && data.map((item, index) => (
                <AlertsGridRow key={item.rentaId}
                               item={item}
                               index={index}
                               expanded={expandedItems.has(item.rentaId)}
                               selected={selectedItemKeys.has(item.rentaId)}
                               details={deviceDetailsMap[item.rentaId]}
                               onRowCheckboxClick={() => handleRowCheckboxClick(item.rentaId)}
                               onAccordionButtonClick={() => handleAccordionButtonClick(item.rentaId)}
                               onSaveSubscriptionsAsync={(subscriptions) => saveDeviceSubscriptions(item, subscriptions)}
                />
            ))}
        </div>
    );
};

export default AlertsGrid;